import { Check, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { apiClient } from "~/lib/api-client";
import {
  llmPlatformDisplayLabel,
  llmPlatformLogoSrc,
} from "~/lib/llm-platform";

interface PlatformOption {
  id: number;
  name: string;
  tracking_method: string;
}

interface TrackedPlatformRow {
  answer_engine_id: number;
  name: string;
  tracking_method: string;
}

interface ModelSelectorProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  className?: string;
}

export default function ModelSelector({
  value,
  onValueChange,
  className,
}: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [platforms, setPlatforms] = useState<PlatformOption[]>([]);
  const [trackedLoaded, setTrackedLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/ai-visibility/tracked-platforms");
        const rows = (res.data?.data || []) as TrackedPlatformRow[];
        const mapped = rows.map((r) => ({
          id: r.answer_engine_id,
          name: r.name,
          tracking_method: r.tracking_method,
        }));
        mapped.sort((a, b) =>
          llmPlatformDisplayLabel(a.name).localeCompare(
            llmPlatformDisplayLabel(b.name)
          )
        );
        if (!cancelled) {
          setPlatforms(mapped);
          setTrackedLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPlatforms([]);
          setTrackedLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!trackedLoaded || value === null) return;
    if (platforms.length === 0) {
      onValueChange(null);
      return;
    }
    const allowed = new Set(platforms.map((p) => String(p.id)));
    if (!allowed.has(value)) {
      onValueChange(null);
    }
  }, [trackedLoaded, platforms, value, onValueChange]);

  const selectedValue = value || "all";
  const selectedPlatform =
    selectedValue === "all"
      ? null
      : platforms.find((p) => String(p.id) === selectedValue);
  const selectedLabel =
    selectedValue === "all"
      ? "All Models"
      : selectedPlatform
        ? llmPlatformDisplayLabel(selectedPlatform.name)
        : platforms.length === 0
          ? "Models"
          : `Model ${selectedValue}`;
  const selectedLogo =
    selectedValue !== "all" && selectedPlatform
      ? llmPlatformLogoSrc(selectedPlatform.name)
      : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className}>
          {selectedLogo ? (
            <img
              src={selectedLogo}
              alt=""
              className="w-4 h-4 object-contain mr-2"
            />
          ) : (
            <Sparkles className="w-4 h-4 text-muted-foreground mr-2" />
          )}
          <span className="text-sm">{selectedLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="start">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => {
              onValueChange(null);
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer"
          >
            <Sparkles className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-left">All Models</span>
            {selectedValue === "all" && (
              <Check className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          {platforms.map((p) => {
            const idStr = String(p.id);
            const logo = llmPlatformLogoSrc(p.name);
            const label = llmPlatformDisplayLabel(p.name);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onValueChange(idStr);
                  setOpen(false);
                }}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer"
              >
                {logo ? (
                  <img src={logo} alt="" className="w-4 h-4 object-contain" />
                ) : (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-semibold text-muted-foreground">
                    {label.slice(0, 1)}
                  </span>
                )}
                <span className="flex-1 text-left">{label}</span>
                {selectedValue === idStr && (
                  <Check className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
